import { useCallback, useEffect, useState } from 'react';

/**
 * Keeps the current view in the URL hash.
 *
 * Two bugs this fixes, both of which make an app feel broken rather than
 * merely inconvenient:
 *
 *  1. Refreshing threw you back to the home view, because the view lived only
 *     in React state. Anyone who reloads to check fresh data lost their place.
 *  2. The browser Back button left the app entirely. With no history entries of
 *     our own, "back" from the first screen you saw is whatever came before the
 *     app — so Back logged people out of their own dashboard.
 *
 * A hash rather than a path deliberately: it needs no server rewrite rules, and
 * it cannot 404 on a deep link if the hosting config ever changes.
 *
 * pushState for navigation (so Back walks your history) and replaceState when
 * correcting an unknown hash (so a typo does not become a history entry you
 * have to click through).
 */
export function useHashView<T extends string>(valid: readonly T[], fallback: T) {
  const parse = useCallback((): T => {
    const raw = decodeURIComponent(window.location.hash.replace(/^#\/?/, '')).trim();
    const head = raw.split('/')[0] as T;
    return valid.includes(head) ? head : fallback;
  }, [valid, fallback]);

  const [view, setViewState] = useState<T>(parse);

  useEffect(() => {
    // Normalise on first load so the URL always reflects what is on screen —
    // otherwise the first Back press is a no-op that looks like a freeze.
    const current = decodeURIComponent(window.location.hash.replace(/^#\/?/, ''));
    if (current !== view) {
      window.history.replaceState(null, '', '#/' + view);
    }
    const onPop = () => setViewState(parse());
    window.addEventListener('popstate', onPop);
    window.addEventListener('hashchange', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('hashchange', onPop);
    };
    // Intentionally once: later changes go through setView.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setView = useCallback((next: T) => {
    if (next === view) return;
    window.history.pushState(null, '', '#/' + next);
    setViewState(next);
  }, [view]);

  return [view, setView] as const;
}
